/**
 * Admin API client — thin fetch wrappers for /api/admin/* routes.
 * Used only by the AdminApp (platform admin surface).
 */

export interface AdminAccount {
  id: string;
  slug: string;
  name: string;
  status: string;
  tier: string;
  currency: string;
  createdAt: string;
  updatedAt: string;
  stripeAccountId: string | null;
  stripeChargesEnabled: number;
  stripePayoutsEnabled: number;
  customHostname: string | null;
  customHostnameStatus: string | null;
}

export interface AdminAccountStats {
  customersCount: number;
  bookingsCount: number;
  lastBookingAt: string | null;
  activeMembershipsCount: number;
  lastActivityAt: string | null;
  ownerEmail: string | null;
  ownerName: string | null;
}

export interface AdminAccountDetail {
  account: AdminAccount;
  stats: AdminAccountStats;
}

export interface AdminAccountListResponse {
  rows: AdminAccount[];
  total: number;
  limit: number;
  offset: number;
}

export interface PlatformStats {
  totalAccounts: number;
  activeAccounts: number;
  trialAccounts: number;
  signupsToday: number;
  signupsThisWeek: number;
  signupsThisMonth: number;
  churnThisMonth: number;
}

export interface AdminActivityLogEntry {
  id: string;
  accountId: string;
  actorMembershipId: string | null;
  action: string;
  subjectType: string;
  subjectId: string;
  metadata: unknown;
  createdAt: string;
}

export interface ImpersonateResponse {
  token: string;
  accountId: string;
  accountSlug: string;
  accountName: string;
  expiresAt: string;
}

const BASE = '';

async function adminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export const adminApi = {
  getPlatformStats(): Promise<PlatformStats> {
    return adminFetch('/api/admin/platform-stats');
  },

  listAccounts(params: {
    status?: string;
    tier?: string;
    search?: string;
    limit?: number;
    offset?: number;
    sort?: string;
  }): Promise<AdminAccountListResponse> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.tier) qs.set('tier', params.tier);
    if (params.search) qs.set('search', params.search);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.offset !== undefined) qs.set('offset', String(params.offset));
    if (params.sort) qs.set('sort', params.sort);
    const query = qs.toString();
    return adminFetch(`/api/admin/accounts${query ? `?${query}` : ''}`);
  },

  getAccount(id: string): Promise<AdminAccountDetail> {
    return adminFetch(`/api/admin/accounts/${id}`);
  },

  transitionStatus(
    id: string,
    toStatus: string,
    reason: string,
  ): Promise<{ transitioned: boolean; from: string; to: string }> {
    return adminFetch(`/api/admin/accounts/${id}/transition-status`, {
      method: 'POST',
      body: JSON.stringify({ toStatus, reason }),
    });
  },

  impersonate(id: string): Promise<ImpersonateResponse> {
    return adminFetch(`/api/admin/accounts/${id}/impersonate`, {
      method: 'POST',
    });
  },

  getActivityLog(id: string): Promise<{ rows: AdminActivityLogEntry[] }> {
    return adminFetch(`/api/admin/accounts/${id}/activity-log`);
  },

  updateAccount(
    id: string,
    patch: { tier?: 'starter' | 'pro' | 'enterprise'; extendTrialDays?: number },
  ): Promise<{ account: AdminAccount }> {
    return adminFetch(`/api/admin/accounts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },

  deleteAccount(id: string, confirmSlug: string): Promise<{ deleted: boolean; accountId: string }> {
    return adminFetch(`/api/admin/accounts/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ confirmSlug }),
    });
  },

  provisionTenant(input: {
    accountSlug: string;
    accountName: string;
    ownerEmail: string;
    ownerName: string;
    currency: string;
    defaultTimezone: string;
    locale: string;
    firstFacilitySlug: string;
    firstFacilityName: string;
  }): Promise<{ account: AdminAccount }> {
    return adminFetch('/api/admin/provision', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};
